import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { colors, typography } from '../../../styles/theme';

/** Alto de la fila título + acciones. */
export const PANEL_HEADER_BAR_HEIGHT = 68;

/** border/divider. */
const BORDER_DIVIDER = '#383838';

/**
 * Tipografía compartida de headers de paneles mapa.
 * Exportada para títulos custom (detalle, chips, etc.) sin duplicar sx.
 */
export const panelTitleSx = {
  color: colors.textPanelTitle,
  fontFamily: typography.fontFamily.heading,
  fontWeight: typography.fontWeight.semibold,
  fontSize: '20px',
  lineHeight: '20px',
  letterSpacing: '0.1px',
  m: 0,
};

export const panelDescriptionSx = {
  color: colors.textPanelDescription,
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.regular,
  fontSize: '14px',
  lineHeight: '19px',
  letterSpacing: 0,
  m: 0,
};

/** Subtítulo bajo el título y encima del divider (menos protagonismo que description). */
export const panelHeaderSubtitleSx = {
  color: 'rgba(255,255,255,0.45)',
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.regular,
  fontSize: '13px',
  lineHeight: '18px',
  letterSpacing: 0,
  m: 0,
};

export const panelHeaderSubtitleWrapSx = {
  px: 2.5,
  pt: 0,
  pb: 1.5,
  mt: -1.75,
};

export const panelHeaderBarSx = {
  height: PANEL_HEADER_BAR_HEIGHT,
  minHeight: PANEL_HEADER_BAR_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 2.5,
  gap: 1,
  flexShrink: 0,
  boxSizing: 'border-box',
};

export const panelHeaderDividerSx = {
  borderColor: BORDER_DIVIDER,
};

export const panelHeaderDescriptionWrapSx = {
  px: 2.5,
  pt: 1.5,
  pb: 1.5,
};

/** Cruz de cierre: hit 24×24, glifo 14×14 inset 5px — color primary/on-primary. */
export const panelCloseIconButtonSx = {
  width: 24,
  height: 24,
  minWidth: 24,
  p: '5px',
  boxSizing: 'border-box',
  color: '#E8F4F4',
  opacity: 1,
  '&:hover': {
    color: '#E8F4F4',
    bgcolor: 'rgba(232,244,244,0.08)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
    width: 18,
    height: 18,
  },
};

const renderTextOrNode = (value, sx, { component = 'p', variant = 'body2' } = {}) => {
  if (value == null || value === false || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number') {
    return (
      <Typography component={component} variant={variant} sx={sx}>
        {value}
      </Typography>
    );
  }
  return value;
};

/**
 * Solo tipografía título (+ adornment). Preferí `PanelHeader` para el chrome completo.
 */
export const PanelHeaderText = ({ title, startAdornment = null, sx = undefined }) => (
  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
    {startAdornment}
    {renderTextOrNode(title, panelTitleSx, { component: 'h2', variant: 'h6' })}
  </Box>
);

/**
 * Header estándar de paneles mapa:
 * [ título | actions ]
 * ──────── divider ────
 * descripción (opcional, siempre debajo del divider)
 *
 * @param {string|React.ReactNode} title
 * @param {string|React.ReactNode} [description]
 * @param {string|React.ReactNode} [subtitle] — debajo del título, encima del divider
 * @param {React.ReactNode} [startAdornment]
 * @param {React.ReactNode} [actions]
 * @param {object} [sx]
 */
const PanelHeader = ({
  title,
  description,
  subtitle,
  startAdornment = null,
  actions = null,
  sx = undefined,
}) => {
  const descriptionNode = renderTextOrNode(description, panelDescriptionSx);
  const subtitleNode = renderTextOrNode(subtitle, panelHeaderSubtitleSx);

  return (
    <Box sx={{ flexShrink: 0, ...sx }}>
      <Box sx={panelHeaderBarSx}>
        <PanelHeaderText title={title} startAdornment={startAdornment} />
        {actions != null && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
              alignSelf: 'center',
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
      {subtitleNode != null && (
        <Box sx={panelHeaderSubtitleWrapSx}>{subtitleNode}</Box>
      )}
      <Divider sx={panelHeaderDividerSx} />
      {descriptionNode != null && (
        <Box sx={panelHeaderDescriptionWrapSx}>{descriptionNode}</Box>
      )}
    </Box>
  );
};

export default PanelHeader;
